import React, { FC, useEffect, useMemo } from "react";
import { IState } from "store/store.interface";
import { useSelector, useDispatch } from "react-redux";
import { IRestrictionField } from "../Restrictions/RestrictionsBlock";
import { ECampaignMode } from "store/reducers/promotion.reducers";
import RadioGroup from "components/RadioGroup";
import { setPromotionObject } from "store/actions/promotion.actions";
import { Box, Text } from "common";

interface IProps {
  field: IRestrictionField;
  selectOptions: any;
  getValue: (keys: string[]) => string | Date;
}

const RecurrenceRadio: FC<IProps> = ({ field, selectOptions, getValue }) => {
  const radioType = field.name === "budget" ? "budgetType" : "usageType";

  const dispatch = useDispatch();
  const { campaignMode } = useSelector((state: IState) => state.promotion);

  const { usageValue, usageType, budgetValue, budgetType } = useMemo(() => {
    return {
      usageValue: getValue(["restrictions", "numberOfUsage"]),
      usageType: getValue(["restrictions", "usageType"]),
      budgetValue: getValue(["restrictions", "budget"]),
      budgetType: getValue(["restrictions", "budgetType"]),
    };
  }, [getValue]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setPromotionObject(event.target.value, ["restrictions", radioType]),
    );
  };

  // This useEffect will clear the radio type if corresponding value is removed
  useEffect(() => {
    if (!budgetValue && budgetType) {
      dispatch(setPromotionObject("", ["restrictions", "budgetType"]));
    }
    if (!usageValue && usageType) {
      dispatch(setPromotionObject("", ["restrictions", "usageType"]));
    }
  }, [budgetValue, budgetType, usageValue, usageType, dispatch]);

  const options = useMemo(() => {
    const optionTypes =
      field.name === "budget"
        ? "recurrenceBudgetTypes"
        : "recurrenceUsageTypes";

    return selectOptions[optionTypes] || [];
  }, [selectOptions, field]);

  const showError =
    (field.name === "numberOfUsage" && usageValue && !usageType) ||
    (field.name === "budget" && budgetValue && !budgetType);

  if (
    campaignMode === ECampaignMode.RECURRING &&
    !["numberOfUsage", "budget"].includes(field?.name)
  ) {
    return null;
  }

  if (campaignMode !== ECampaignMode.RECURRING) {
    return null;
  }

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Text fontSize={14} mr={1}>
          {field.name === "budget"
            ? "Choose Budget Handling - "
            : "Choose Usage Handling - "}
        </Text>
        <RadioGroup
          testId="recurrence-radio-group"
          value={getValue(["restrictions", radioType]) || ""}
          onChange={handleRadioChange}
          options={options}
        />
      </Box>
      {showError && (
        <Text color="error.main" style={{ position: "relative", top: "-20px" }}>
          Please choose how this limit should be applied.
        </Text>
      )}
    </>
  );
};

export default RecurrenceRadio;
